'use client';

import { compact } from 'es-toolkit';
import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FooterClient } from '@/components/common/FooterClient';
import { ThemeAwareLogo } from '@/components/common/ThemeAwareLogo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { pushSweetLinkCliError } from '@/lib/sweetlink/cli-error-log';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { copyToClipboard } from '@/lib/utils/clipboard';

type CopyStatus = 'idle' | 'copied' | 'failed';

type ErrorBoundaryFallbackProperties = {
  error: Error & { digest?: string };
  onReset?: () => void;
};

const getCopyButtonLabel = (status: CopyStatus): string => {
  if (status === 'copied') {
    return 'Copied';
  }
  if (status === 'failed') {
    return 'Copy failed';
  }
  return 'Copy error details';
};

export function ErrorBoundaryFallback({ error, onReset }: ErrorBoundaryFallbackProperties) {
  const { isAuthenticated } = useAuth();
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  const lastLoggedErrorReference = useRef<Error | null>(null);
  if (lastLoggedErrorReference.current !== error) {
    console.error(error);
    lastLoggedErrorReference.current = error;
  }

  const pushedErrorReference = useRef<Error | null>(null);
  const browserWindow = (globalThis as typeof globalThis & { window?: Window }).window;
  if (browserWindow !== undefined && pushedErrorReference.current !== error) {
    // Also mirror marketing/global errors into the SweetLink CLI feed so mcporter can display them.
    pushSweetLinkCliError({
      type: 'global-error',
      message: error?.message ?? 'Unknown error',
      stack: error?.stack ?? null,
      source: error?.digest ?? null,
    });
    pushedErrorReference.current = error;
  }

  const handlePrimaryAction = useCallback(() => {
    if (onReset) {
      onReset();
      return;
    }
    const win = getBrowserWindow();
    if (win) {
      win.location.reload();
    }
  }, [onReset]);

  const primaryActionLabel = onReset ? 'Try again' : 'Reload page';
  const homeHref = isAuthenticated ? '/timeline' : '/';

  const copyErrorDetails = useCallback(async () => {
    const message = error.message || 'An unknown error occurred.';
    const details = compact([
      `Message: ${message}`,
      error.digest ? `Reference code: ${error.digest}` : null,
      error.stack ? `Stack trace:\n${error.stack}` : null,
    ]).join('\n\n');

    if (!details) {
      setCopyStatus('failed');
      return;
    }

    const success = await copyToClipboard(details, {
      onSuccess: () => {
        setCopyStatus('copied');
      },
      onError: (copyError: unknown) => {
        console.error('Failed to copy error details', copyError);
      },
    });
    if (!success) {
      setCopyStatus('failed');
    }
  }, [error]);
  const handleCopyDetails = useCallback(() => {
    copyErrorDetails().catch((copyError: unknown) => {
      console.error('Unable to copy error details', copyError);
    });
  }, [copyErrorDetails]);

  useEffect(() => {
    if (copyStatus === 'idle') {
      return;
    }

    const win = getBrowserWindow();
    const timeoutId = win?.setTimeout(() => {
      setCopyStatus('idle');
    }, 2000);

    return () => {
      if (timeoutId !== null && win) {
        win.clearTimeout(timeoutId);
      }
    };
  }, [copyStatus]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-layer-chrome w-full border-b border-border/60 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/80 shadow-sm">
          <div className="container mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <ThemeAwareLogo alt="Sweetistics" width={28} height={28} className="h-7 w-7 object-contain" />
              <span className="text-base font-semibold tracking-tight">Sweetistics</span>
            </div>
            <span className="text-xs font-medium text-muted-foreground">Error</span>
          </div>
        </header>
        <main className="container flex flex-1 items-center justify-center py-16">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>We ran into an unexpected error while loading this page.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>{error.message || 'An unknown error occurred.'}</p>
              {error.digest ? <p className="text-xs">Reference code: {error.digest}</p> : null}
            </CardContent>
            <CardFooter className="flex flex-wrap gap-2">
              <Button onClick={handlePrimaryAction}>{primaryActionLabel}</Button>
              <Button variant="secondary" onClick={handleCopyDetails}>
                {getCopyButtonLabel(copyStatus)}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const win = getBrowserWindow();
                  if (win) {
                    win.location.href = homeHref;
                  }
                }}
              >
                Go home
              </Button>
            </CardFooter>
          </Card>
        </main>
      </div>
      <FooterClient variant="static" isAuthenticated={isAuthenticated} />
    </div>
  );
}
